import { useStripeConnection } from '@/stores/stripe.store'
import { getQuotePaymentScheduleValidationIssue } from '@/lib/quote-payment-schedule-validation'
import { getQuoteBuilderChecklist, type QuoteBuilderChecklistItem } from '@/components/quotes/quote-builder-readiness'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import type { QuoteEditorFormValues, QuoteEditorTotals } from './quote-editor-form'

export interface QuoteReadiness {
  /** The "before you send" checklist: title, contents, schedule, Stripe. */
  checklist: QuoteBuilderChecklistItem[]
  /** A paid quote can't be sent until Stripe is connected. */
  stripeBlocksSend: boolean
}

/**
 * Whether the quote on screen is ready to send, from the editor's current
 * values. Shared by the editor's send button and the summary rail so the two
 * never disagree.
 */
export function useQuoteReadiness({
  organizationId,
  values,
  totals,
  allLineItems,
}: {
  organizationId: string
  values: QuoteEditorFormValues
  totals: QuoteEditorTotals
  allLineItems: QuoteLineItem[]
}): QuoteReadiness {
  const { isConnected: isStripeConnected, hasLoaded: stripeHasLoaded } = useStripeConnection(organizationId)
  const stripeBlocksSend = stripeHasLoaded && totals.totalCents > 0 && !isStripeConnected
  const checklist = getQuoteBuilderChecklist({
    title: values.title,
    itemCount: allLineItems.length,
    eventCount: values.events.length,
    paymentScheduleIssueMessage: getQuotePaymentScheduleValidationIssue(values.paymentSchedules, totals.totalCents)?.message,
    // Only a payable quote needs Stripe.
    isStripeConnected: !stripeBlocksSend,
  })
  return { checklist, stripeBlocksSend }
}
